<script setup>
import { pickValueFromPlace, validateNoPOBox } from '#common/utils/address'

const props = defineProps({
  idPrefix: {
    type: String,
    required: true,
  },
  stateOptions: {
    type: Object,
    required: true,
  },
  stateCodes: {
    type: Object,
    required: true,
  },
  allowAlaskaHawaii: {
    type: Boolean,
    required: false,
    default: false,
  },
})
const emit = defineEmits(['addressAutocomplete'])
const metadata = useMetadata()
function autoCompletePlaceChanged({ place }) {
  emit('addressAutocomplete', {
    line1: `${pickValueFromPlace(place, 'street_number').short_name} ${
      pickValueFromPlace(place, 'route').long_name
    }`.trim(),
    line4: pickValueFromPlace(place, 'locality').long_name,
    state_name: pickValueFromPlace(place, 'administrative_area_level_1')
      .long_name,
    postcode: pickValueFromPlace(place, 'postal_code').short_name,
  })
}
function nonDtcState(theState) {
  const stateCode = props.stateCodes[theState.value]
  const nonDtcStates =
    metadata.settingsVars.COMPLIANCE_STATE_GROUPS.non_dtc.concat(
      metadata.settingsVars.COMPLIANCE_STATE_GROUPS.private_label_only,
    )
  let isValid = true
  nonDtcStates.forEach((uspsCode) => {
    if (stateCode === uspsCode) {
      isValid = false
    }
  })
  return isValid
}
function nonAlaskaHawaii(theState) {
  return (
    props.allowAlaskaHawaii ||
    (props.stateCodes[theState.value] !== 'AK' &&
      props.stateCodes[theState.value] !== 'HI')
  )
}
function notPOBox({ value }) {
  return validateNoPOBox(value, false)
}
</script>

<template>
  <div>
    <div class="columns small-12">
      <div class="floating">
        <MapsAutocompleteAddressInputFK
          :id="`${idPrefix}-address-line-1`"
          name="line1"
          autocomplete="address-line1"
          validation="bail|required|notPOBox"
          :validation-rules="{ notPOBox }"
          placeholder="Address (We cannot ship to P.O. boxes)"
          label="Address (We cannot ship to P.O. boxes)"
          :validation-messages="{
            required: 'Error: Address is required.',
            notPOBox: 'Error: We cannot ship to P.O. boxes',
          }"
          @onAddressSelected="autoCompletePlaceChanged"
        />
      </div>
    </div>
    <div class="columns small-12">
      <div class="floating">
        <FloatingInputFK
          :id="`${idPrefix}-address-line-2`"
          name="line2"
          autocomplete="address-line2"
          placeholder="Apt, suite, etc (Optional)"
          label="Apt, suite, etc (Optional)"
        />
      </div>
    </div>
    <div class="columns medium-4">
      <div class="floating">
        <FloatingInputFK
          :id="`${idPrefix}-city`"
          name="line4"
          autocomplete="address-level2"
          validation="required"
          placeholder="City"
          label="City"
        />
      </div>
    </div>
    <div class="columns medium-4">
      <div class="floating">
        <FloatingInputFK
          :id="`${idPrefix}-state`"
          type="select"
          input-class="floating__select"
          :options="stateOptions"
          name="state_name"
          validation-visibility="dirty"
          validation="required|nonDtcState|nonAlaskaHawaii"
          :validation-rules="{
            nonDtcState,
            nonAlaskaHawaii,
          }"
          :validation-messages="{
            nonDtcState: ({ node }) =>
              `Error: Due to state law, we cannot ship to ${node.value}`,
            nonAlaskaHawaii: ({ node }) =>
              `Error: Gift subscriptions cannot be shipped to ${node.value}. For assistance, please call our Member Services team at ${metadata.customerService.numberDisplay} ${metadata.customerService.hoursShort}.`,
          }"
          placeholder=" "
          label="State"
        />
      </div>
    </div>
    <div class="columns medium-4">
      <div class="floating">
        <FloatingInputFK
          :id="`${idPrefix}-postcode`"
          name="postcode"
          validation="bail|required|matches:/^\d{5}(?:-\d{4})?$/"
          placeholder="Zip/Postal Code"
          label="Zip/Postal Code"
          :validation-messages="{
            matches: 'Error: You must input a valid US Zip Code',
          }"
        />
      </div>
    </div>
  </div>
</template>
